import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { cn } from '@/lib/cn';
import { Spinner } from './Spinner';

type Variant = 'primary' | 'secondary' | 'success' | 'danger' | 'ghost';
type Size = 'sm' | 'md';

const variants: Record<Variant, string> = {
  primary: 'bg-brand text-white border-brand hover:bg-brand/90',
  secondary: 'bg-white text-ink border-line hover:bg-soft',
  success: 'bg-teal text-white border-teal hover:bg-teal/90',
  danger: 'bg-danger-50 text-danger border-danger/25 hover:bg-danger/10',
  ghost: 'bg-transparent text-ink border-transparent hover:bg-soft',
};

const sizes: Record<Size, string> = {
  sm: 'px-2.5 py-1.5 text-[11px] rounded-[8px]',
  md: 'px-3.5 py-2.5 text-xs rounded-[9px]',
};

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
  ref?: Ref<HTMLButtonElement>;
}

export function Button({ variant = 'secondary', size = 'md', loading, icon, className, children, disabled, type = 'button', ...rest }: Props) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 border font-semibold whitespace-nowrap transition disabled:cursor-not-allowed disabled:opacity-50',
        variants[variant],
        sizes[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner className="size-3.5" /> : icon}
      {children}
    </button>
  );
}
